import type { AuthedRequester } from "@/lib/brokerAuth";

// The broker's notification-preference seam — `/api/broker/notification-preferences`
// (PMS-S351 read / S357 write). Types mirror the PMS contract. A mandatory
// (locked) transactional event cannot be turned off — the server 400s it — so the
// only real opt-outs are the advisory events here and marketing consent (the
// separate brokerConsent seam). Consumed by the preference center (BRK-S158).

const BASE = "/api/broker/notification-preferences";

export type NotificationChannelKey = "email" | "sms" | "in_app";

/** Whether PMS can deliver on a channel today (email + in_app now; sms waits on S040). */
export interface NotificationChannelAvailability {
  channel: NotificationChannelKey;
  available: boolean;
}

export interface NotificationPreferenceEvent {
  noticeType: string;
  label: string;
  /** The effective on/off for this broker. */
  enabled: boolean;
  /** Mandatory events are locked (cannot be turned off). */
  locked: boolean;
}

export interface NotificationPreferenceCategory {
  category: string;
  label: string;
  events: NotificationPreferenceEvent[];
}

export interface NotificationPreferencesView {
  channels: NotificationChannelAvailability[];
  categories: NotificationPreferenceCategory[];
}

/** GET — the broker's own preference view (category → events, each enabled/locked). */
export function getNotificationPreferences(
  request: AuthedRequester,
): Promise<NotificationPreferencesView> {
  return request<NotificationPreferencesView>(BASE);
}

/** PUT — toggle one event; returns the refreshed view. The server 400s an attempt
 *  to turn a mandatory (locked) event off. */
export function setEventEnabled(
  request: AuthedRequester,
  input: { noticeType: string; enabled: boolean },
): Promise<NotificationPreferencesView> {
  return request<NotificationPreferencesView>(BASE, {
    method: "PUT",
    body: input,
  });
}
